/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-22
 */
import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, TimeoutError, timeout } from 'rxjs';
import { HttpService } from '../http/http.service';
import { IaConversarRequest, IaConversarResponse, IaCuotaResponse, IaMensaje } from './ia.models';

/**
 * Base de la IA en nebula-tasks, relativa como las demas: el servicio HTTP pone el gateway y el
 * interceptor el token. No va en API_CONFIG: sus productos son los modulos de fecha del sistema.
 */
export const IA_BASE = '/nebula-tasks/api/v1/ia';

/** Espera de una respuesta: por encima de los 110 s del servidor, para que el corte sea suyo. */
export const IA_ESPERA_CONVERSAR_MS = 120_000;

/** Espera de la consulta de la cuota. */
export const IA_ESPERA_CUOTA_MS = 10_000;

/** Turnos de historial que acepta el servidor; se recorta igual antes de enviar. */
export const IA_TOPE_HISTORIAL = 20;

/** Largo maximo de la pregunta, el mismo del servidor. */
export const IA_TOPE_PREGUNTA = 4000;

/** Largo maximo del contexto de la opcion, el mismo del servidor. */
export const IA_TOPE_CONTEXTO = 6000;

/**
 * Por que no se pudo conversar, para elegir el mensaje (siempre del front, nunca el del servidor) y
 * si el panel queda deshabilitado.
 */
export type IaFallo =
  | 'cuota-agotada'
  | 'sin-configuracion'
  | 'clave-sin-registrar'
  | 'no-disponible'
  | 'clave-rechazada'
  | 'tiempo-agotado'
  | 'error';

/** Fallos con los que el panel deja de aceptar preguntas hasta que cambie la configuracion. */
export const IA_FALLOS_QUE_DESHABILITAN: readonly IaFallo[] = [
  'cuota-agotada',
  'sin-configuracion',
  'clave-sin-registrar',
];

/**
 * Traduce el error de una llamada al fallo del contrato de nebula-tasks.
 *
 * @param error lo que emitio la llamada
 * @returns el fallo que corresponde
 */
export function iaFalloDe(error: unknown): IaFallo {
  if (error instanceof TimeoutError) return 'tiempo-agotado';
  if (!(error instanceof HttpErrorResponse)) return 'error';
  switch (error.status) {
    case 429:
      return 'cuota-agotada';
    case 404:
      return 'sin-configuracion';
    case 409:
      return 'clave-sin-registrar';
    case 503:
      return 'no-disponible';
    case 502:
      return 'clave-rechazada';
    case 504:
      return 'tiempo-agotado';
    default:
      return 'error';
  }
}

/**
 * Recorta el historial a los ultimos turnos que acepta el servidor.
 *
 * @param historial la conversacion del panel, del mas antiguo al mas reciente
 * @returns los ultimos {@link IA_TOPE_HISTORIAL}
 */
export function recortarHistorial(historial: readonly IaMensaje[]): IaMensaje[] {
  return historial.slice(-IA_TOPE_HISTORIAL);
}

/**
 * IA del tenant en nebula-tasks, por el gateway y con el token del usuario (adaptado de
 * `AiWebService` de simappe-shared: respuesta desenvuelta, tiempos de espera y cuota para
 * deshabilitar la interfaz).
 */
@Injectable({ providedIn: 'root' })
export class IaService {
  private readonly http = inject(HttpService);

  /**
   * Pregunta a la IA con el historial del panel y el contexto de la opcion abierta.
   *
   * @param request la pregunta, el historial, el contexto y el nombre de la opcion
   * @returns la respuesta y el consumo
   */
  conversar(request: IaConversarRequest): Observable<IaConversarResponse> {
    const cuerpo: IaConversarRequest = {
      ...request,
      historial: recortarHistorial(request.historial),
      aiContext: request.aiContext ? request.aiContext.slice(0, IA_TOPE_CONTEXTO) : null,
    };
    return this.http
      .postContent<IaConversarResponse>(`${IA_BASE}/conversar`, cuerpo)
      .pipe(timeout(IA_ESPERA_CONVERSAR_MS));
  }

  /**
   * Cuota mensual de IA del tenant del token.
   *
   * @returns la cuota, con `agotada` y `activa` para deshabilitar el panel
   */
  cuota(): Observable<IaCuotaResponse> {
    return this.http
      .getContent<IaCuotaResponse>(`${IA_BASE}/cuota`)
      .pipe(timeout(IA_ESPERA_CUOTA_MS));
  }
}
